import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore, firebaseAuth } from '../firebase';
import './CouponDialog.css';

const CouponDialog = ({ isOpen, onClose, product, onCouponApply }) => {
  const [couponCode, setCouponCode] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const userSignin = useSelector(state => state.userSignin);
  const { userInfo } = userSignin;
  const currentUser = firebaseAuth.currentUser;

  useEffect(() => {
    if (isOpen && currentUser) {
      loadAvailableCoupons();
    }
  }, [isOpen, currentUser]);

  const loadAvailableCoupons = async () => {
    try {
      setLoading(true);
      
      // Load user's available coupons
      const userCouponsRef = collection(firestore, 'USERS', currentUser.uid, 'MY_COUPONS');
      const userCouponsSnapshot = await getDocs(userCouponsRef);
      
      const coupons = [];
      for (const doc of userCouponsSnapshot.docs) {
        const couponData = doc.data();
        if (couponData.coupon_id) {
          // Get coupon details
          const couponDetailsRef = doc(firestore, 'COUPONS', couponData.coupon_id);
          const couponDetailsDoc = await getDoc(couponDetailsRef);
          
          if (couponDetailsDoc.exists()) {
            const details = couponDetailsDoc.data();
            coupons.push({
              id: couponData.coupon_id,
              ...details,
              userCouponData: couponData
            });
          }
        }
      }
      
      setAvailableCoupons(coupons);
    } catch (error) {
      console.error('Error loading coupons:', error);
      setError('Failed to load available coupons');
    } finally {
      setLoading(false);
    }
  };

  const validateCoupon = async (couponId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Check if coupon exists
      const couponRef = doc(firestore, 'COUPONS', couponId);
      const couponDoc = await getDoc(couponRef);

      if (!couponDoc.exists()) {
        setError('Invalid coupon code');
        return;
      }

      const couponData = couponDoc.data();
      
      // Check if user has this coupon
      const userCouponRef = collection(firestore, 'USERS', currentUser.uid, 'MY_COUPONS');
      const userCouponQuery = query(userCouponRef, where('coupon_id', '==', couponId));
      const userCouponSnapshot = await getDocs(userCouponQuery);

      if (userCouponSnapshot.empty) {
        setError('You do not have this coupon');
        return;
      }

      const userCouponData = userCouponSnapshot.docs[0].data();

      // Check if coupon is already used
      if (userCouponData.already_used) {
        setError('This coupon has already been used');
        return;
      }

      // Check expiry date
      if (couponData.expire_date && new Date(couponData.expire_date.toDate()) < new Date()) {
        setError('This coupon has expired');
        return;
      }

      // Check minimum order value
      const productPrice = parseFloat(product.product_price) || 0;
      const minOrderValue = parseFloat(couponData.min_order_amount) || 0;
      
      if (productPrice < minOrderValue) {
        setError(`Minimum order value should be ₹${minOrderValue}`);
        return;
      }

      // Calculate discount
      let discountAmount = 0;
      if (couponData.discount_type === 'percentage') {
        discountAmount = (productPrice * parseFloat(couponData.discount)) / 100;
        if (couponData.max_discount_amount) {
          discountAmount = Math.min(discountAmount, parseFloat(couponData.max_discount_amount));
        }
      } else {
        discountAmount = parseFloat(couponData.discount) || 0;
      }

      const finalPrice = Math.max(0, productPrice - discountAmount);

      setSuccess(`Coupon applied! You save ₹${discountAmount.toFixed(2)}`);
      
      // Apply coupon
      onCouponApply({
        couponId,
        couponData,
        discountAmount,
        finalPrice,
        originalPrice: productPrice
      });

    } catch (error) {
      console.error('Error validating coupon:', error);
      setError('Failed to validate coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleManualCouponApply = () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }
    validateCoupon(couponCode.trim().toUpperCase());
  };

  const handleCouponSelect = (coupon) => {
    validateCoupon(coupon.id);
  };

  if (!isOpen) return null;

  return (
    <div className="coupon-dialog-overlay" onClick={onClose}>
      <div className="coupon-dialog" onClick={e => e.stopPropagation()}>
        <div className="coupon-dialog-header">
          <h3>Apply Coupon</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="coupon-dialog-content">
          {/* Manual Coupon Entry */}
          <div className="manual-coupon-section">
            <h4>Enter Coupon Code</h4>
            <div className="coupon-input-group">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="coupon-input"
              />
              <button 
                className="apply-btn"
                onClick={handleManualCouponApply}
                disabled={loading}
              >
                {loading ? 'Applying...' : 'Apply'}
              </button>
            </div>
          </div>

          {/* Available Coupons */}
          <div className="available-coupons-section">
            <h4>Available Coupons</h4>
            {loading && availableCoupons.length === 0 ? (
              <div className="loading-coupons">Loading coupons...</div>
            ) : availableCoupons.length > 0 ? (
              <div className="coupons-list">
                {availableCoupons.map(coupon => (
                  <div key={coupon.id} className="coupon-card">
                    <div className="coupon-info">
                      <div className="coupon-title">{coupon.coupon_title}</div>
                      <div className="coupon-description">{coupon.description}</div>
                      <div className="coupon-details">
                        <span className="discount-text">
                          {coupon.discount_type === 'percentage' 
                            ? `${coupon.discount}% OFF` 
                            : `₹${coupon.discount} OFF`}
                        </span>
                        {coupon.min_order_amount && (
                          <span className="min-order">
                            Min order: ₹{coupon.min_order_amount}
                          </span>
                        )}
                      </div>
                      {coupon.expire_date && (
                        <div className="expiry-date">
                          Expires: {new Date(coupon.expire_date.toDate()).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button 
                      className="select-coupon-btn"
                      onClick={() => handleCouponSelect(coupon)}
                      disabled={loading}
                    >
                      Apply
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-coupons">No coupons available</div>
            )}
          </div>

          {/* Messages */}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>
      </div>
    </div>
  );
};

export default CouponDialog;
